import React, { useState } from 'react';
import history from '../history';
import StatusView from '../live/StatusView';
import { getProductConfig } from '../backend/productConfig';
import Sheet from './Sheet';
import './mobileMoreMenu.css';

/**
 * MobileMoreMenu — shared More menu for mobile bottom nav.
 * Used by LiveView (legacy single-cam PlaybackView is retired).
 *
 * Props:
 *   sName      — server name for header
 *   platform   — 'Ios' | 'Mac' | 'Android' | 'Win' | 'Linux'
 *   fEview     — boolean, enterprise mode
 *   sSess      — session key
 *   logoutUser — logout action
 *   onClose    — close handler
 */
const MobileMoreMenu = ({ sName, platform, fEview, sSess, logoutUser, onClose, onSetup }) => {

  const [showStatus, setShowStatus] = useState(false);
  // Cloud is the hosted product — hide ALL standalone viewer download links (App Store, mobile app, Full Viewer).
  const { hasViewerDownloads } = getProductConfig();

  const handleLogout = () => {
    onClose();
    logoutUser(sSess);
    history.push('/');
  };

  return (
    <Sheet className="mobile-more-menu" scrim={false} title={sName || 'Menu'} ariaLabel={sName || 'Menu'} onClose={onClose}>
      <div className="mobile-more-menu__actions">

        {/* System Status — collapsible panel reusing the desktop StatusView */}
        <button
          className="mobile-more-menu__action"
          onClick={() => setShowStatus(s => !s)}
          aria-expanded={showStatus}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
          System Status
          <span className="mobile-more-menu__chevron" aria-hidden="true">
            {showStatus ? '▾' : '▸'}
          </span>
        </button>
        {showStatus && (
          <div className="mobile-more-menu__status-panel">
            <StatusView />
          </div>
        )}

        {/* Platform-aware viewer download — hidden entirely in cloud (hasViewerDownloads=false). */}
        { hasViewerDownloads && (platform === 'Ios' ? (
          <a href="https://itunes.apple.com/us/app/dividia-viewer/id1143269725?mt=8"
            target="_blank" rel="noopener noreferrer"
            className="mobile-more-menu__action">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Download iOS App
          </a>
        ) : platform === 'Mac' ? (
          <a href="https://geo.itunes.apple.com/us/app/dividia-viewer/id1130011776?mt=12"
            target="_blank" rel="noopener noreferrer"
            className="mobile-more-menu__action">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Download Mac App
          </a>
        ) : platform === 'Android' ? (
          <a href="https://play.google.com/store/apps/details?id=net.dividia.dview"
            target="_blank" rel="noopener noreferrer"
            className="mobile-more-menu__action">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
            </svg>
            Download Android App
          </a>
        ) : (
          <a href={fEview
                    ? (platform === 'Win' ? '/elauncher.php' : '/eview.php')
                    : (platform === 'Win' ? '/launcher.php' : '/dview.php')}
            target="_blank" rel="noopener noreferrer"
            className="mobile-more-menu__action">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 21v-2h2V5c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2v1h2c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2h-2v5h2v2H2zm14-10h2V8h-2v3zM6 19h10V5H6v14z"/>
            </svg>
            {fEview ? 'Download Enterprise Viewer' : 'Download Full Viewer'}
          </a>
        ))}

        {/* Setup */}
        <button className="mobile-more-menu__action"
          onClick={() => { onClose(); if (onSetup) onSetup(); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/>
          </svg>
          Setup
        </button>

        {/* Logout */}
        <button className="mobile-more-menu__action mobile-more-menu__action--danger"
          onClick={handleLogout}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <polyline points="16,17 21,12 16,7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Logout
        </button>
      </div>
    </Sheet>
  );
};

export default MobileMoreMenu;
